import { useMemo, useState } from "react";
import { fmtHM, fmtPct, agentRows, tlRows } from "./adhUtils";

const card = "mk-card";

const SortTh = ({ label, k, sort, setSort, right = true }) => (
  <th onClick={() => setSort((s) => ({ k, dir: s.k === k && s.dir === -1 ? 1 : -1 }))}
    className={`font-mono-hud cursor-pointer select-none px-4 py-2.5 text-[9px] uppercase tracking-[0.12em] text-thdim hover:text-thbody ${right ? "text-right" : "text-left"}`}>
    {label}{sort.k === k ? (sort.dir === -1 ? " ↓" : " ↑") : ""}
  </th>
);

const sortRows = (rows, { k, dir }) => [...rows].sort((a, b) => {
  const va = a[k], vb = b[k];
  if (va == null) return 1;
  if (vb == null) return -1;
  return (typeof va === "string" ? va.localeCompare(vb) : va - vb) * dir;
});

export default function AdhTlAgents({ data, slice, onOpenAgent }) {
  const [gSort, setGSort] = useState({ k: "monFriPct", dir: 1 });
  const [aSort, setASort] = useState({ k: "pctMet", dir: 1 });
  const [selTl, setSelTl] = useState("");
  const [q, setQ] = useState("");

  const agents = useMemo(() => agentRows(slice, data.agents, data.targets), [slice, data]);
  const tls = useMemo(() => tlRows(agents.filter((a) => a.tl !== "—")), [agents]);

  const filtered = useMemo(() => {
    let rows = agents;
    if (selTl) rows = rows.filter((r) => r.tl === selTl);
    if (q) rows = rows.filter((r) => (r.name + r.c).toLowerCase().includes(q.toLowerCase()));
    return rows;
  }, [agents, selTl, q]);

  return (
    <div className="space-y-6">
      <div className={card} data-testid="adh-tl-table">
        <p className="font-mono-hud px-5 pt-4 text-[10px] uppercase tracking-[0.25em] text-thdim">TL League · click a row to filter agents</p>
        <div className="mt-3 max-h-[420px] overflow-auto">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-thcard"><tr className="border-b border-thborder/10">
              <SortTh label="Team Leader" k="tl" sort={gSort} setSort={setGSort} right={false} />
              <SortTh label="Agents" k="hc" sort={gSort} setSort={setGSort} />
              <SortTh label="Mon–Fri att." k="monFriPct" sort={gSort} setSort={setGSort} />
              <SortTh label="Days ≥ bar" k="pctMet" sort={gSort} setSort={setGSort} />
              <SortTh label="Late %" k="latePct" sort={gSort} setSort={setGSort} />
              <SortTh label="Median hrs" k="medMin" sort={gSort} setSort={setGSort} />
              <SortTh label="Corrupt punches" k="corrupt" sort={gSort} setSort={setGSort} />
            </tr></thead>
            <tbody>
              {sortRows(tls, gSort).map((r) => (
                <tr key={r.tl} data-testid={`adh-tl-row-${r.tl.replace(/\s+/g, "-")}`}
                  onClick={() => setSelTl(r.tl === selTl ? "" : r.tl)}
                  className={`cursor-pointer border-b border-thborder/5 hover:bg-thhover/5 ${selTl === r.tl ? "bg-thcyan/5" : ""}`}>
                  <td className="max-w-[220px] truncate px-4 py-2.5 text-xs font-medium text-thstrong">{r.tl}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{r.hc}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.monFriPct)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.pctMet)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-[#F59E0B]">{fmtPct(r.latePct)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtHM(r.medMin)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-[#F43F5E]">{r.corrupt || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={card} data-testid="adh-agent-table">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
          <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">
            Agents{selTl ? ` · ${selTl}` : ""} · click a row for the full discipline report
          </p>
          <input data-testid="adh-agent-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search agent…"
            className="rounded-md border border-thborder/10 bg-thinput px-3 py-1.5 text-xs text-thstrong placeholder:text-thdim focus:border-thcyan focus:outline-none" />
        </div>
        <div className="mt-3 max-h-[520px] overflow-auto">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-thcard"><tr className="border-b border-thborder/10">
              <SortTh label="Agent" k="name" sort={aSort} setSort={setASort} right={false} />
              <SortTh label="TL" k="tl" sort={aSort} setSort={setASort} right={false} />
              <SortTh label="Site" k="site" sort={aSort} setSort={setASort} right={false} />
              <SortTh label="Present" k="present" sort={aSort} setSort={setASort} />
              <SortTh label="Mon–Fri att." k="monFriPct" sort={aSort} setSort={setASort} />
              <SortTh label="Days ≥ bar" k="pctMet" sort={aSort} setSort={setASort} />
              <SortTh label="Late %" k="latePct" sort={aSort} setSort={setASort} />
              <SortTh label="Median hrs" k="medMin" sort={aSort} setSort={setASort} />
            </tr></thead>
            <tbody>
              {sortRows(filtered, aSort).map((r) => (
                <tr key={r.c} data-testid={`adh-agent-row-${r.c}`} onClick={() => onOpenAgent(r.c)}
                  className="cursor-pointer border-b border-thborder/5 hover:bg-thhover/5">
                  <td className="max-w-[200px] truncate px-4 py-2.5 text-xs font-medium text-thstrong">{r.name}</td>
                  <td className="max-w-[160px] truncate px-4 py-2.5 text-xs text-thmute">{r.tl}</td>
                  <td className="px-4 py-2.5 text-xs text-thmute">{r.site}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{r.present}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.monFriPct)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.pctMet)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-[#F59E0B]">{fmtPct(r.latePct)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtHM(r.medMin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
